import React, { useState, useEffect } from 'react';
import { getApiBaseUrl } from '../../utils/apiUtils';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar, Doughnut, Pie } from 'react-chartjs-2';
import { 
  FaChartLine, 
  FaUsers, 
  FaMoneyBillWave, 
  FaMapMarkerAlt,
  FaClock,
  FaShieldAlt,
  FaDownload,
  FaSync,
  FaEye,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle,
  FaPercentage,
  FaCalendarAlt,
  FaGlobe,
  FaUserFriends,
  FaGift,
  FaChartBar,
  FaArrowUp,
  FaArrowDown
} from 'react-icons/fa';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const CouponAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('30d');

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/coupon-analytics/comprehensive`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setAnalytics(data.data);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const formatCurrency = (amount) => {
    const numericAmount = parseFloat(amount) || 0;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(numericAmount);
  };

  const formatNumber = (num) => {
    const numericValue = parseFloat(num) || 0;
    return new Intl.NumberFormat('en-IN').format(numericValue);
  };

  const formatPercentage = (num) => {
    const numericValue = parseFloat(num) || 0;
    return `${numericValue.toFixed(2)}%`;
  };

  // Chart configurations
  const dailyTrendsChart = {
    data: {
      labels: analytics?.time_season_analytics?.daily_trends?.map(item => 
        new Date(item.date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })
      ) || [],
      datasets: [
        {
          label: 'Coupon Usage',
          data: analytics?.time_season_analytics?.daily_trends?.map(item => item.usage_count) || [],
          borderColor: 'rgb(99, 102, 241)',
          backgroundColor: 'rgba(99, 102, 241, 0.1)',
          fill: true,
          tension: 0.4
        },
        {
          label: 'Total Discount',
          data: analytics?.time_season_analytics?.daily_trends?.map(item => item.total_discount) || [],
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          fill: true,
          tension: 0.4,
          yAxisID: 'y1'
        }
      ]
    },
    options: {
      responsive: true,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      scales: {
        y: {
          type: 'linear',
          display: true,
          position: 'left',
          title: {
            display: true,
            text: 'Usage Count'
          }
        },
        y1: {
          type: 'linear',
          display: true,
          position: 'right',
          title: {
            display: true,
            text: 'Discount Amount (₹)'
          },
          grid: {
            drawOnChartArea: false,
          },
        },
      },
      plugins: {
        title: {
          display: true,
          text: 'Daily Coupon Usage Trends'
        }
      }
    }
  };

  const couponTypeChart = {
    data: {
      labels: analytics?.coupon_type_performance?.discount_type_performance?.map(item => item.type) || [],
      datasets: [{
        data: analytics?.coupon_type_performance?.discount_type_performance?.map(item => item.usage_count) || [],
        backgroundColor: [
          'rgba(99, 102, 241, 0.8)',
          'rgba(34, 197, 94, 0.8)',
          'rgba(251, 146, 60, 0.8)',
          'rgba(239, 68, 68, 0.8)',
          'rgba(168, 85, 247, 0.8)'
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }]
    },
    options: {
      responsive: true,
      plugins: {
        legend: {
          position: 'bottom'
        },
        title: {
          display: true,
          text: 'Coupon Type Performance'
        }
      }
    }
  };

  const hourlyPatternsChart = {
    data: {
      labels: analytics?.time_season_analytics?.hourly_patterns?.map(item => `${item.hour}:00`) || [],
      datasets: [{
        label: 'Usage Count',
        data: analytics?.time_season_analytics?.hourly_patterns?.map(item => item.usage_count) || [],
        backgroundColor: 'rgba(99, 102, 241, 0.8)',
        borderColor: 'rgb(99, 102, 241)',
        borderWidth: 1
      }]
    },
    options: {
      responsive: true,
      plugins: {
        title: {
          display: true,
          text: 'Hourly Usage Patterns'
        }
      },
      scales: {
        y: {
          beginAtZero: true
        }
      }
    }
  };

  const topCouponsChart = {
    data: {
      labels: analytics?.coupon_type_performance?.top_coupons?.slice(0, 10).map(item => item.code) || [],
      datasets: [{
        label: 'Usage Count',
        data: analytics?.coupon_type_performance?.top_coupons?.slice(0, 10).map(item => item.usage_count) || [],
        backgroundColor: 'rgba(34, 197, 94, 0.8)',
        borderColor: 'rgb(34, 197, 94)',
        borderWidth: 1
      }]
    },
    options: {
      responsive: true,
      plugins: {
        title: {
          display: true,
          text: 'Top 10 Most Popular Coupons'
        }
      },
      scales: {
        y: {
          beginAtZero: true
        }
      }
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FaChartLine },
    { id: 'usage', label: 'Usage Metrics', icon: FaUsers },
    { id: 'financial', label: 'Financial', icon: FaMoneyBillWave },
    { id: 'performance', label: 'Performance', icon: FaChartBar },
    { id: 'trends', label: 'Trends', icon: FaChartLine },
    { id: 'location', label: 'Location', icon: FaMapMarkerAlt },
    { id: 'fraud', label: 'Fraud & Abuse', icon: FaShieldAlt }
  ];

  const MetricCard = ({ title, value, subtitle, icon: Icon, color = 'blue', trend = null }) => {
    const colorClasses = {
      blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
      red: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
      yellow: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400',
      purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
      indigo: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400',
      pink: 'bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400',
      gray: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
    };
    
    const borderColorClasses = {
      blue: 'border-l-blue-500 dark:border-l-blue-400',
      green: 'border-l-green-500 dark:border-l-green-400',
      red: 'border-l-red-500 dark:border-l-red-400',
      yellow: 'border-l-yellow-500 dark:border-l-yellow-400',
      purple: 'border-l-purple-500 dark:border-l-purple-400',
      indigo: 'border-l-indigo-500 dark:border-l-indigo-400',
      pink: 'border-l-pink-500 dark:border-l-pink-400',
      gray: 'border-l-gray-500 dark:border-l-gray-400'
    };

    return (
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6 border-l-4 hover:shadow-xl transition-all duration-300 ${borderColorClasses[color] || borderColorClasses.blue}`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-300">{title}</p>
            <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
            {subtitle && <p className="text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>}
            {trend && (
              <div className={`flex items-center mt-2 text-sm ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {trend > 0 ? <FaArrowUp className="mr-1" /> : <FaArrowDown className="mr-1" />}
                {Math.abs(trend)}%
              </div>
            )}
          </div>
          <div className={`p-3 rounded-full ${colorClasses[color] || colorClasses.blue}`}>
            <Icon className="text-xl sm:text-2xl" />
          </div>
        </div>
      </div>
    );
  };

  const StatCard = ({ title, value, icon: Icon, color = 'blue' }) => {
    const colorClasses = {
      blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
      red: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
      yellow: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400',
      purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
      indigo: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400',
      pink: 'bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400',
      gray: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
    };

    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 hover:shadow-xl transition-all duration-300">
        <div className="flex items-center">
          <div className={`p-2 rounded-full ${colorClasses[color] || colorClasses.blue} mr-3`}>
            <Icon className="text-lg" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-300">{title}</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">{value}</p>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 dark:border-blue-400"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="text-center">
          <FaExclamationTriangle className="text-red-500 dark:text-red-400 text-2xl sm:text-4xl mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Error Loading Analytics</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">{error}</p>
          <button
            onClick={fetchAnalytics}
            className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-2 rounded hover:from-blue-600 hover:to-blue-700 shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <FaSync className="inline mr-2" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent">
                Coupon Analytics Dashboard
              </h1>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 mt-2">Comprehensive insights into coupon performance and user behavior</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
              <button
                onClick={fetchAnalytics}
                className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:from-blue-600 hover:to-blue-700 flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 text-sm sm:text-base"
              >
                <FaSync className="mr-2" />
                <span className="hidden sm:inline">Refresh</span>
                <span className="sm:hidden">Refresh</span>
              </button>
              <button className="bg-gradient-to-r from-green-500 to-green-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:from-green-600 hover:to-green-700 flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 text-sm sm:text-base">
                <FaDownload className="mr-2" />
                <span className="hidden sm:inline">Export</span>
                <span className="sm:hidden">Export</span>
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg mb-6 border border-gray-100 dark:border-gray-700">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex overflow-x-auto scrollbar-hide px-4 sm:px-6">
              <div className="flex space-x-2 sm:space-x-8 min-w-max">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-3 sm:py-4 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm flex items-center transition-all duration-300 whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-400'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:border-blue-300 dark:hover:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                    }`}
                  >
                    <tab.icon className="mr-1 sm:mr-2 h-4 w-4" />
                    <span className="hidden sm:inline">{tab.label}</span>
                    <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                  </button>
                ))}
              </div>
            </nav>
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* General Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:p-6">
              <MetricCard
                title="Total Coupons"
                value={formatNumber(analytics?.general_metrics?.total_coupons)}
                icon={FaGift}
                color="blue"
              />
              <MetricCard
                title="Active Coupons"
                value={formatNumber(analytics?.general_metrics?.active_coupons)}
                icon={FaCheckCircle}
                color="green"
              />
              <MetricCard
                title="Redemption Rate"
                value={formatPercentage(analytics?.general_metrics?.redemption_percentage)}
                icon={FaPercentage}
                color="purple"
              />
              <MetricCard
                title="Total Discount Given"
                value={formatCurrency(analytics?.financial_metrics?.total_discount_given)}
                icon={FaMoneyBillWave}
                color="yellow"
              />
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:p-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6 border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-all duration-300">
                <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">Daily Usage Trends</h3>
                <Line {...dailyTrendsChart} />
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6 border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-all duration-300">
                <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">Coupon Type Distribution</h3>
                <Doughnut {...couponTypeChart} />
              </div>
            </div>

            {/* Usage Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:p-6">
              <StatCard
                title="Total Redemptions"
                value={formatNumber(analytics?.usage_metrics?.total_redemptions)}
                icon={FaUsers}
                color="blue"
              />
              <StatCard
                title="Unique Users"
                value={formatNumber(analytics?.usage_metrics?.unique_users_redeemed)}
                icon={FaUserFriends}
                color="green"
              />
              <StatCard
                title="Avg Redemptions/User"
                value={analytics?.usage_metrics?.average_redemptions_per_user?.toFixed(2) || '0'}
                icon={FaChartBar}
                color="purple"
              />
            </div>
          </div>
        )}

        {/* Usage Metrics Tab */}
        {activeTab === 'usage' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:p-6">
              <MetricCard
                title="New Users Acquired"
                value={formatNumber(analytics?.usage_metrics?.new_users_acquired)}
                subtitle="Through coupon usage"
                icon={FaUsers}
                color="green"
              />
              <MetricCard
                title="Existing Customers"
                value={formatNumber(analytics?.usage_metrics?.existing_customers_repeat)}
                subtitle="Repeat purchases with coupons"
                icon={FaUserFriends}
                color="blue"
              />
              <MetricCard
                title="First-Time User Coupons"
                value={formatNumber(analytics?.usage_metrics?.first_time_user_coupons?.total_usage)}
                subtitle={`${formatNumber(analytics?.usage_metrics?.first_time_user_coupons?.unique_users)} unique users`}
                icon={FaGift}
                color="purple"
              />
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6 border border-gray-100 dark:border-gray-700">
              <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">Hourly Usage Patterns</h3>
              <Bar {...hourlyPatternsChart} />
            </div>
          </div>
        )}

        {/* Financial Tab */}
        {activeTab === 'financial' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:p-6">
              <MetricCard
                title="Revenue from Coupon Orders"
                value={formatCurrency(analytics?.financial_metrics?.revenue_from_coupon_orders)}
                icon={FaMoneyBillWave}
                color="green"
              />
              <MetricCard
                title="Coupon AOV"
                value={formatCurrency(analytics?.financial_metrics?.coupon_aov)}
                subtitle="Average Order Value"
                icon={FaChartBar}
                color="blue"
              />
              <MetricCard
                title="Non-Coupon AOV"
                value={formatCurrency(analytics?.financial_metrics?.non_coupon_aov)}
                subtitle="For comparison"
                icon={FaChartLine}
                color="purple"
              />
              <MetricCard
                title="ROI Percentage"
                value={formatPercentage(analytics?.financial_metrics?.roi_percentage)}
                icon={FaChartLine}
                color="yellow"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6 border border-gray-100 dark:border-gray-700">
                <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">Discount Analysis</h3>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-700 dark:text-gray-300">Max Discount Consumed:</span>
                    <span className="font-semibold text-gray-900 dark:text-white">{formatCurrency(analytics?.financial_metrics?.max_discount_consumed)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700 dark:text-gray-300">Average Discount:</span>
                    <span className="font-semibold text-gray-900 dark:text-white">{formatCurrency(analytics?.financial_metrics?.avg_discount_amount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700 dark:text-gray-300">Total Coupon Orders:</span>
                    <span className="font-semibold text-gray-900 dark:text-white">{formatNumber(analytics?.financial_metrics?.total_coupon_orders)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Performance Tab */}
        {activeTab === 'performance' && (
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6 border border-gray-100 dark:border-gray-700">
              <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">Top Performing Coupons</h3>
              <Bar {...topCouponsChart} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6 border border-gray-100 dark:border-gray-700">
                <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">Least Used Coupons</h3>
                <div className="space-y-2">
                  {analytics?.coupon_type_performance?.least_used_coupons?.slice(0, 5).map((coupon, index) => (
                    <div key={index} className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700 rounded">
                      <span className="font-medium text-gray-900 dark:text-white">{coupon.code}</span>
                      <span className="text-sm text-gray-600 dark:text-gray-300">{coupon.usage_count} uses</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6 border border-gray-100 dark:border-gray-700">
                <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">Campaign Effectiveness</h3>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-700 dark:text-gray-300">Total Distributed:</span>
                    <span className="font-semibold text-gray-900 dark:text-white">{formatNumber(analytics?.campaign_effectiveness?.total_distributed)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700 dark:text-gray-300">Total Redeemed:</span>
                    <span className="font-semibold text-gray-900 dark:text-white">{formatNumber(analytics?.campaign_effectiveness?.total_redeemed)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700 dark:text-gray-300">Redemption Rate:</span>
                    <span className="font-semibold text-gray-900 dark:text-white">{formatPercentage(analytics?.campaign_effectiveness?.redemption_rate)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700 dark:text-gray-300">Abandoned Cart Recovered:</span>
                    <span className="font-semibold text-gray-900 dark:text-white">{formatNumber(analytics?.campaign_effectiveness?.abandoned_cart_recovered)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Trends Tab */}
        {activeTab === 'trends' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:p-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6 border border-gray-100 dark:border-gray-700">
                <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">Monthly Trends</h3>
                <Bar
                  data={{
                    labels: analytics?.time_season_analytics?.monthly_trends?.map(item => item.month) || [],
                    datasets: [{
                      label: 'Usage Count',
                      data: analytics?.time_season_analytics?.monthly_trends?.map(item => item.usage_count) || [],
                      backgroundColor: 'rgba(99, 102, 241, 0.8)',
                      borderColor: 'rgb(99, 102, 241)',
                      borderWidth: 1
                    }]
                  }}
                  options={{
                    responsive: true,
                    plugins: {
                      title: {
                        display: true,
                        text: 'Monthly Usage Trends'
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true
                      }
                    }
                  }}
                />
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6 border border-gray-100 dark:border-gray-700">
                <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">Weekly Trends</h3>
                <Bar
                  data={{
                    labels: analytics?.time_season_analytics?.weekly_trends?.map(item => `Week ${item.week}`) || [],
                    datasets: [{
                      label: 'Usage Count',
                      data: analytics?.time_season_analytics?.weekly_trends?.map(item => item.usage_count) || [],
                      backgroundColor: 'rgba(34, 197, 94, 0.8)',
                      borderColor: 'rgb(34, 197, 94)',
                      borderWidth: 1
                    }]
                  }}
                  options={{
                    responsive: true,
                    plugins: {
                      title: {
                        display: true,
                        text: 'Weekly Usage Trends'
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true
                      }
                    }
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Location Tab */}
        {activeTab === 'location' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:p-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6 border border-gray-100 dark:border-gray-700">
                <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">Top Cities</h3>
                <div className="space-y-2">
                  {analytics?.location_analytics?.top_cities?.slice(0, 10).map((city, index) => (
                    <div key={index} className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700 rounded">
                      <span className="font-medium text-gray-900 dark:text-white">{city.city}</span>
                      <div className="text-right">
                        <div className="font-semibold text-gray-900 dark:text-white">{formatNumber(city.usage_count)} uses</div>
                        <div className="text-sm text-gray-600 dark:text-gray-300">{formatCurrency(city.total_discount)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6 border border-gray-100 dark:border-gray-700">
                <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">Top States</h3>
                <div className="space-y-2">
                  {analytics?.location_analytics?.top_states?.slice(0, 10).map((state, index) => (
                    <div key={index} className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700 rounded">
                      <span className="font-medium text-gray-900 dark:text-white">{state.state}</span>
                      <div className="text-right">
                        <div className="font-semibold text-gray-900 dark:text-white">{formatNumber(state.usage_count)} uses</div>
                        <div className="text-sm text-gray-600 dark:text-gray-300">{formatCurrency(state.total_discount)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Fraud & Abuse Tab */}
        {activeTab === 'fraud' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:p-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6 border border-gray-100 dark:border-gray-700">
                <h3 className="text-lg font-semibold mb-4 text-red-600 dark:text-red-400">Multiple Redemptions</h3>
                <div className="space-y-2">
                  {analytics?.fraud_abuse?.multiple_redemptions?.slice(0, 5).map((user, index) => (
                    <div key={index} className="flex justify-between items-center p-2 bg-red-50 dark:bg-red-900/20 rounded">
                      <span className="font-medium text-sm text-gray-900 dark:text-white">{user.email}</span>
                      <span className="text-red-600 dark:text-red-400 font-semibold">{user.redemption_count} uses</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6 border border-gray-100 dark:border-gray-700">
                <h3 className="text-lg font-semibold mb-4 text-orange-600 dark:text-orange-400">High Discount Abuse</h3>
                <div className="space-y-2">
                  {analytics?.fraud_abuse?.high_discount_abuse?.slice(0, 5).map((user, index) => (
                    <div key={index} className="flex justify-between items-center p-2 bg-orange-50 dark:bg-orange-900/20 rounded">
                      <span className="font-medium text-sm text-gray-900 dark:text-white">{user.email}</span>
                      <div className="text-right">
                        <div className="text-orange-600 dark:text-orange-400 font-semibold">{formatCurrency(user.total_discount)}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-300">{user.usage_count} uses</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6 border border-gray-100 dark:border-gray-700">
                <h3 className="text-lg font-semibold mb-4 text-purple-600 dark:text-purple-400">Suspicious Activity</h3>
                <div className="space-y-2">
                  {analytics?.fraud_abuse?.suspicious_activity?.slice(0, 5).map((activity, index) => (
                    <div key={index} className="flex justify-between items-center p-2 bg-purple-50 dark:bg-purple-900/20 rounded">
                      <span className="font-medium text-sm text-gray-900 dark:text-white">{activity.activity_type}</span>
                      <div className="text-right">
                        <div className="text-purple-600 dark:text-purple-400 font-semibold">{activity.account_count} accounts</div>
                        <div className="text-sm text-gray-600 dark:text-gray-300">{activity.total_redemptions} redemptions</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CouponAnalytics;
